import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, List, ListItemAvatar, ListItemText, Divider, CircularProgress, Button } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import Modal from '../../../Modal/Modal';
import {
  requestShiftSwapThunk,
  acceptShiftSwapThunk,
  declineShiftSwapThunk,
  fetchShiftSwapRequestThunk,
} from '../../../../redux/shifts/shiftSwapThunks';
import {
  selectShiftSwapLoading,
  selectShiftSwapRequestStatus,
  selectCurrentShiftSwapRequest,
  resetRequestStatus,
} from '../../../../redux/shifts/shiftSwapSlice';
import { selectHasPrivilegedRole, selectIsCurrentUserOwner } from '../../../../redux/slices/currentUserSlice';
import { showSuccessToast, showErrorToast } from '../../../../utils/toastUtils';
import { updateShiftWithPendingSwap, clearShiftPendingSwap } from '../../../../redux/shifts/shiftsSlice';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';
import {
  ShiftSwapModalContent,
  LoadingContainer,
  LargeAvatar,
  ShiftDetailsBox,
  ShiftDetailRow,
  StyledListItem,
  EmployeeAvatar,
  EmployeeName,
  EmployeeShiftTypes,
  ConfirmationTitle,
  SelectedEmployeeBox,
  SelectedEmployeeAvatar,
  SelectedEmployeeName,
  ConfirmationMessage,
  ActionButtonsContainer,
} from './ShiftSwapModal.styles';

/**
 * ShiftSwapModal component for both requesting and responding to shift swaps
 * Mode 'request': Allows employees to select another employee to request a shift swap with
 * Mode 'respond': Shows swap request details with accept/decline buttons (like iOS)
 * For owners/managers, allows direct shift swapping without employee acceptance
 */
const ShiftSwapModal = ({
  open,
  onClose,
  mode = 'request', // 'request' or 'respond'
  shift,
  employees = [],
  swapRequest = null, // for respond mode
  onActionComplete = () => {},
  onRequestSuccess = () => {}, // callback for successful swap request
}) => {
  const dispatch = useDispatch();
  const { formatTime, formatTimeRange } = useTimeFormatters(); // Use venue timezone formatters
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const isOwner = useSelector(selectIsCurrentUserOwner);
  const isLoading = useSelector(selectShiftSwapLoading);
  const requestStatus = useSelector(selectShiftSwapRequestStatus);
  const currentUserId = useSelector((state) => state.currentUser?.employeeInfo?.id);
  const fetchedRequest = useSelector(selectCurrentShiftSwapRequest);

  const canDirectSwap = hasPrivilegedRole || isOwner;

  // Filter employees to show potential swap candidates
  const availableEmployees = employees.filter((employee) => {
    // Don't show the current user themselves
    if (employee.id === currentUserId) return false;

    // For shift swaps, we can show all employees (they don't need to have the exact same shift type)
    // The backend will handle validation of shift compatibility
    return true;
  });

  // Reset request status when modal opens for new request
  useEffect(() => {
    if (open && mode === 'request') {
      dispatch(resetRequestStatus());
    }
  }, [open, mode, dispatch]);

  // Fetch swap request details if we're in respond mode and only have the ID
  useEffect(() => {
    if (mode === 'respond' && swapRequest?.id && !swapRequest?.shift && !fetchedRequest?.shift) {
      dispatch(fetchShiftSwapRequestThunk({ shiftSwapRequestId: swapRequest.id }));
    }
  }, [mode, swapRequest?.id, swapRequest?.shift, fetchedRequest?.shift, dispatch]);

  // Handle successful request
  useEffect(() => {
    if (requestStatus === 'succeeded' && selectedEmployee) {
      const employeeName = selectedEmployee.displayName || selectedEmployee.name;

      if (canDirectSwap) {
        showSuccessToast(`Shifts swapped successfully`);
      } else {
        showSuccessToast(`Shift swap request sent to ${employeeName}`);
      }

      // Call the success callback to trigger data refresh
      onRequestSuccess();

      // Close the modal but keep the optimistic update until real data arrives
      onClose();
      setSelectedEmployee(null);
      setShowConfirmation(false);
    } else if (requestStatus === 'failed') {
      // Clear the optimistic update on failure
      if (shift?.id) {
        dispatch(clearShiftPendingSwap({ shiftId: shift.id }));
      }
      showErrorToast('Failed to process shift swap. Please try again.');
    }
  }, [requestStatus, selectedEmployee, onClose, canDirectSwap, onRequestSuccess, dispatch, shift?.id]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setShowConfirmation(true);
  };

  const handleConfirmRequest = async () => {
    if (!selectedEmployee || !shift) return;

    try {
      // Optimistic update: Immediately show the pending swap status in the UI
      // This provides instant feedback to the user while the request is being processed
      // The real-time listener will replace this with the actual data when it arrives
      dispatch(
        updateShiftWithPendingSwap({
          shiftId: shift.id,
          pendingSwapId: 'pending', // Temporary ID until real data arrives
          selectedEmployee, // Pass the selected employee for immediate display
        })
      );

      await dispatch(
        requestShiftSwapThunk({
          shiftId: shift.id,
          targetEmployeeId: selectedEmployee.id,
        })
      ).unwrap();
    } catch (error) {
      // Error handling is done through the status effect
      console.error('Failed to request shift swap:', error);
    }
  };

  const handleAcceptSwap = async () => {
    if (!swapRequest?.id) return;

    try {
      await dispatch(
        acceptShiftSwapThunk({
          shiftSwapRequestId: swapRequest.id,
        })
      ).unwrap();
      showSuccessToast('Shift swap accepted successfully');
      onActionComplete('accepted');
      onClose();
    } catch (error) {
      showErrorToast(`Failed to accept shift swap: ${error}`);
    }
  };

  const handleDeclineSwap = async () => {
    if (!swapRequest?.id) return;

    try {
      await dispatch(
        declineShiftSwapThunk({
          shiftSwapRequestId: swapRequest.id,
        })
      ).unwrap();
      showSuccessToast('Shift swap declined');
      onActionComplete('declined');
      onClose();
    } catch (error) {
      showErrorToast(`Failed to decline shift swap: ${error}`);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setSelectedEmployee(null);
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedEmployee(null);
    setShowConfirmation(false);
  };

  const handleModalClick = (event) => {
    event.stopPropagation();
  };

  if (!shift && mode === 'request') return null;
  if (!swapRequest && mode === 'respond') return null;

  // Response mode - show accept/decline UI (like iOS DetailView)
  if (mode === 'respond') {
    const responseShift = swapRequest?.shift || fetchedRequest?.shift;
    const { sourceName } = swapRequest;

    return (
      <Modal
        open={open}
        onClose={handleCloseModal}
        width={400}
        maxHeight="80vh"
        alignContent="center"
        ariaLabelledBy="shift-swap-response-modal-title"
      >
        {/* Loading state or shift details */}
        {!responseShift ? (
          <LoadingContainer>
            <CircularProgress />
            <Typography sx={{ marginLeft: 2 }}>Loading shift details...</Typography>
          </LoadingContainer>
        ) : (
          <ShiftSwapModalContent onClick={handleModalClick} onMouseDown={handleModalClick} onMouseUp={handleModalClick}>
            {/* Header */}
            <Typography variant="h6" component="h2" textAlign="center" gutterBottom>
              {sourceName}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Avatar */}
            <LargeAvatar src={responseShift.assignedProfileImageUrl} alt={sourceName}>
              <PersonIcon />
            </LargeAvatar>

            {/* Title */}
            <Typography variant="h6" textAlign="center" gutterBottom>
              Take {/* responseShift.start is already in milliseconds (from convertTimestampsToUnix) */}
              {new Date(responseShift.start).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}{' '}
              shift?
            </Typography>

            {/* Shift Type */}
            <Typography variant="body2" color="text.tertiary" textAlign="center" sx={{ mb: 3 }}>
              {responseShift.name} Shift
            </Typography>

            {/* Shift Details */}
            <ShiftDetailsBox>
              <ShiftDetailRow>
                <Typography variant="body2">Shift start</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatTime(responseShift.start)}
                </Typography>
              </ShiftDetailRow>

              <Divider />

              <ShiftDetailRow>
                <Typography variant="body2">Shift end</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {/* responseShift.end is already in milliseconds */}
                  {formatTime(responseShift.end)}
                </Typography>
              </ShiftDetailRow>
            </ShiftDetailsBox>

            {/* Action Buttons */}
            <ActionButtonsContainer>
              <Button variant="primary" fullWidth onClick={handleAcceptSwap} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Accept'}
              </Button>

              <Button variant="primaryDestructive" fullWidth onClick={handleDeclineSwap} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Decline'}
              </Button>
            </ActionButtonsContainer>
          </ShiftSwapModalContent>
        )}
      </Modal>
    );
  }

  // Request mode - existing functionality
  return (
    <>
      {/* Main Modal */}
      <Modal
        open={open && !showConfirmation}
        onClose={handleCloseModal}
        width={500}
        maxHeight="80vh"
        alignContent="left"
        ariaLabelledBy="shift-swap-modal-title"
      >
        <ShiftSwapModalContent>
          {/* Header */}
          <Typography id="shift-swap-modal-title" variant="h6" component="h2">
            Request Shift Swap
          </Typography>

          <Divider />

          {/* Shift Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="medium">
              {shift.name} Shift
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(shift.start).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}{' '}
              • {formatTimeRange(shift.start, shift.end)}
            </Typography>
          </Box>

          {/* Instructions */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {canDirectSwap
              ? 'Select an employee to swap shifts with. The swap will happen immediately.'
              : 'Select an employee to request a shift swap. They will receive a notification to accept or decline.'}
          </Typography>

          {/* Employee List */}
          {availableEmployees.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No employees available for this shift type
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: 'background.secondary' }}>
              {availableEmployees.map((employee) => (
                <StyledListItem key={employee.id} component="button" onClick={() => handleEmployeeSelect(employee)}>
                  <ListItemAvatar>
                    <EmployeeAvatar src={employee.profileImageUrl} alt={employee.displayName}>
                      {!employee.profileImageUrl && <PersonIcon />}
                    </EmployeeAvatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<EmployeeName>{employee.displayName || employee.name}</EmployeeName>}
                    secondary={
                      <EmployeeShiftTypes>
                        {employee.shiftTypes?.join(', ') || 'No shift types listed'}
                      </EmployeeShiftTypes>
                    }
                  />
                </StyledListItem>
              ))}
            </List>
          )}
        </ShiftSwapModalContent>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        width={400}
        maxHeight="60vh"
        alignContent="center"
        ariaLabelledBy="confirm-swap-modal-title"
      >
        <ShiftSwapModalContent>
          {/* Header */}
          <ConfirmationTitle id="confirm-swap-modal-title" variant="h6" component="h2">
            Send Request to {selectedEmployee?.displayName}?
          </ConfirmationTitle>

          <Divider />

          {/* Selected Employee Info */}
          {selectedEmployee && (
            <SelectedEmployeeBox>
              <SelectedEmployeeAvatar src={selectedEmployee.profileImageUrl} alt={selectedEmployee.displayName}>
                {!selectedEmployee.profileImageUrl && <PersonIcon />}
              </SelectedEmployeeAvatar>
              <Box>
                <SelectedEmployeeName>{selectedEmployee.displayName}</SelectedEmployeeName>
                <Typography variant="body2" color="text.secondary">
                  {selectedEmployee.shiftTypes?.join(', ')}
                </Typography>
              </Box>
            </SelectedEmployeeBox>
          )}

          {/* Confirmation Message */}
          <ConfirmationMessage>
            {canDirectSwap
              ? `The shifts will be swapped immediately between you and ${selectedEmployee?.displayName}.`
              : `${selectedEmployee?.displayName} will receive a notification requesting to take your shift. They can accept or decline the request.`}
          </ConfirmationMessage>

          {/* Action Buttons */}
          <ActionButtonsContainer>
            <Button variant="primary" onClick={handleConfirmRequest} disabled={isLoading} fullWidth>
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : canDirectSwap ? (
                'Swap Shifts'
              ) : (
                'Send Request'
              )}
            </Button>
            <Button variant="primaryDestructive" onClick={handleCloseConfirmation} disabled={isLoading} fullWidth>
              Cancel
            </Button>
          </ActionButtonsContainer>
        </ShiftSwapModalContent>
      </Modal>
    </>
  );
};

export default ShiftSwapModal;
